const AUTH_BASE_URL = import.meta.env.VITE_AUTH_URL || "https://auth.trustngolabs.com/auth";
const AUTH_ENTRY_BASE_URL = import.meta.env.VITE_AUTH_ENTRY_URL || new URL(AUTH_BASE_URL).origin;
const APP_BASE_URL = (import.meta.env.VITE_APP_URL || window.location.origin).replace(/\/+$/, "");
const CLIENT_ID = "sigmacert";
const SIGMACERT_TENANT = "sigmacert";
export const PLATFORM_SUPER_ADMIN_EMAIL = "sigmacert1@gmail.com";
type TrustNGOAuthMode = "login" | "signup";

export interface TrustNGOUser {
  name?: string;
  email?: string;
  org_id?: string;
  org_name?: string;
  role?: string;
  user_id?: string;
  tenant?: string;
  tenants?: string[];
  roles?: { [tenant: string]: string };
  plan?: string;
}

export interface SSOSession {
  sub: string;
  email: string;
  tenants: string[];
  roles: { [tenant: string]: string };
  subscription_plan?: string;
  exp: number;
  iat: number;
}

type JWTPayload = Partial<SSOSession> & {
  name?: string;
  full_name?: string;
  user_name?: string;
  email?: string;
  org_id?: string;
  org_name?: string;
  organization?: string;
  role?: string;
  user_id?: string;
  tenant?: string;
  client_id?: string;
  platform_code?: string;
  plan?: string;
  subscription?: string;
};

function decodeJWTPayload(token: string): unknown | null {
  const payload = token.split('.')[1];
  if (!payload) return null;

  const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
  const padded = base64.padEnd(base64.length + (4 - base64.length % 4) % 4, '=');
  return JSON.parse(atob(padded));
}

function normalizeEmail(email?: string | null): string {
  return (email || "").trim().toLowerCase();
}

export function isPlatformSuperAdminEmail(email?: string | null): boolean {
  return normalizeEmail(email) === PLATFORM_SUPER_ADMIN_EMAIL;
}

export function isPlatformSuperAdmin(identity?: Pick<SSOSession, "email"> | Pick<TrustNGOUser, "email"> | null): boolean {
  if (identity?.email) {
    return isPlatformSuperAdminEmail(identity.email);
  }

  const session = getSession();
  if (session?.email) {
    return isPlatformSuperAdminEmail(session.email);
  }

  const user = getCurrentUser();
  return isPlatformSuperAdminEmail(user?.email);
}

/** Decode JWT token (basic implementation) */
function decodeJWT(token: string): SSOSession | null {
  try {
    const decoded = decodeJWTPayload(token) as SSOSession | null;
    return decoded;
  } catch (error) {
    console.error('Failed to decode JWT:', error);
    return null;
  }
}

/** Redirect to TrustNGO SSO login */
export function getTrustNGOAuthUrl(mode: TrustNGOAuthMode = "login", targetPath?: string): string {
  const path = targetPath || (window.location.pathname === "/admin/login" ? "/admin" : window.location.pathname);
  const normalizedPath = path.startsWith("/") ? path : `/${path}`;
  const redirectUrl = `${APP_BASE_URL}${normalizedPath}`;
  const url = new URL("/login", AUTH_ENTRY_BASE_URL);
  url.searchParams.set("client_id", CLIENT_ID);
  url.searchParams.set("redirect_url", redirectUrl);
  if (mode === "signup") {
    url.searchParams.set("intent", "signup");
  }
  return url.toString();
}

export function loginWithTrustNGO(targetPath?: string) {
  window.location.href = getTrustNGOAuthUrl("login", targetPath);
}

/** Redirect to TrustNGO SSO signup */
export function signUpWithTrustNGO() {
  window.location.href = getTrustNGOAuthUrl("signup", "/workspace");
}

/** Clear session and redirect to SSO */
export function logout() {
  localStorage.removeItem("access_token");
  sessionStorage.clear();
  window.location.href = AUTH_BASE_URL;
}

/** Extract and store token from URL after SSO redirect with role-based routing */
export function handleSSOCallback(): string | null {
  const params = new URLSearchParams(window.location.search);
  const token = params.get("token");

  if (token) {
    localStorage.setItem("access_token", token);

    // Decode token to validate session
    const session = decodeJWT(token);

    if (session) {
      // Check if user has sigmacert tenant access. The platform owner is
      // trusted as super admin even before tenant claims are provisioned.
      if (!validateTenantAccess(session).valid) {
        localStorage.removeItem("access_token");
        window.location.href = '/';
        return null;
      }

      // Role-based routing is now handled by PostLoginRouter component
      // Just clean the URL and let the app handle routing
      window.history.replaceState({}, document.title, window.location.pathname);
      return token;
    } else {
      localStorage.removeItem("access_token");
      window.location.href = '/';
    }
  }
  return null;
}

/** Get stored token */
export function getToken(): string | null {
  return localStorage.getItem("access_token");
}

/** Get stored session ID */
export function getSessionId(): string | null {
  return sessionStorage.getItem("session_id");
}

/** Get decoded session from token */
export function getSession(): SSOSession | null {
  const token = getToken();
  if (!token) return null;
  return decodeJWT(token);
}

/** Check if user is authenticated */
export function isAuthenticated(): boolean {
  return !!getToken();
}

/** Decode JWT payload (without verification — verification happens server-side) */
export function decodeToken(token: string): TrustNGOUser | null {
  try {
    const decoded = decodeJWTPayload(token) as JWTPayload | null;
    if (!decoded) return null;
    const email = decoded.email;
    const isSuperAdmin = isPlatformSuperAdminEmail(email);
    return {
      name: decoded.name || decoded.full_name || decoded.user_name,
      email,
      org_id: decoded.org_id,
      org_name: decoded.org_name || decoded.organization,
      role: isSuperAdmin ? "admin" : decoded.role || "user",
      user_id: decoded.sub || decoded.user_id,
      tenant: decoded.tenant || decoded.client_id || decoded.platform_code,
      tenants: decoded.tenants,
      roles: isSuperAdmin
        ? { ...(decoded.roles || {}), [SIGMACERT_TENANT]: "admin" }
        : decoded.roles,
      plan: decoded.plan || decoded.subscription || "free",
    };
  } catch {
    return null;
  }
}

/** Get current user info from token */
export function getCurrentUser(): TrustNGOUser | null {
  const token = getToken();
  if (!token) return null;
  return decodeToken(token);
}

/** Validate tenant access for sigmacert */
export function validateTenantAccess(session: SSOSession | null = getSession()): { valid: boolean; error?: string } {
  if (!session) return { valid: false, error: "NOT_AUTHENTICATED" };

  const now = Math.floor(Date.now() / 1000);
  if (session.exp < now) return { valid: false, error: "SESSION_EXPIRED" };

  if (isPlatformSuperAdminEmail(session.email)) {
    return { valid: true };
  }

  if (!session.tenants?.includes(SIGMACERT_TENANT)) {
    return { valid: false, error: "ACCESS_DENIED" };
  }

  return { valid: true };
}

/** Check if user has admin role */
export function isAdmin(): boolean {
  const session = getSession();
  return hasSigmacertAdminAccess(session);
}

/** Check if a session has SigmaCert admin privileges */
export function hasSigmacertAdminAccess(session: SSOSession | null = getSession()): boolean {
  return isPlatformSuperAdmin(session) || session?.roles?.[SIGMACERT_TENANT] === "admin";
}

/** Get user's plan */
export function getUserPlan(): string {
  const user = getCurrentUser();
  return user?.plan || "free";
}

/** Check feature access based on plan */
export function canAccessFeature(feature: string, usage?: number): boolean {
  const plan = getUserPlan();
  const limits: Record<string, Record<string, number>> = {
    free: { certificates: 10, bulk_issuance: 0, analytics: 0 },
    pro: { certificates: 100, bulk_issuance: 1, analytics: 1 },
    enterprise: { certificates: Infinity, bulk_issuance: 1, analytics: 1 },
  };
  const planLimits = limits[plan] || limits.free;
  if (usage !== undefined) {
    return (planLimits[feature] ?? 0) > usage;
  }
  return (planLimits[feature] ?? 0) > 0;
}

export function getAuthBaseUrl(): string {
  return AUTH_BASE_URL;
}
